import React, { useEffect, useRef, useState } from "react"
import { IoCloseOutline } from "react-icons/io5"
import { useParams } from "react-router-dom"

import { functionsAccessor } from "@firebaseConfig/clientApp"

import { showErrorToast, showSuccessToast } from "./themeToast/toastUtils"

interface ReachOutComponentProps {
  eventId?: string
  featureName: string
  defaultMessage?: string
  isInline?: boolean
  buttonText?: string
  title?: string
  description?: string
  className?: string
}

// Assume showErrorToast, showSuccessToast, and functionsAccessor are already imported

interface ReachOutComponentProps {
  eventId?: string
  featureName: string
  defaultMessage?: string
  isInline?: boolean
  buttonText?: string
  title?: string
  description?: string
  className?: string
}

const ReachOutModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  message: string
  setMessage: (val: string) => void
  isSending: boolean
  onSend: () => void
  title: string
  description: string
}> = ({
  isOpen,
  onClose,
  message,
  setMessage,
  isSending,
  onSend,
  title,
  description,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      const length = el.value.length
      el.setSelectionRange(length, length) // Move cursor to end
    }
  }, [isOpen])

  if (!isOpen) return null

  const mobileView = (
    <div
      className="sm:hidden fixed inset-0 z-[100] flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full bg-[#252525] rounded-t-[32px] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="text-white text-2xl font-medium">{title}</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors bg-transparent p-2 rounded-full bg-white/10"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>
        <div className="px-6 pb-6">
          <p className="text-white/70 text-sm mb-4">{description}</p>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="w-full bg-transparent text-white placeholder-white/50 focus:outline-none text-lg resize-none mt-4"
            autoFocus
            style={{ height: "40vh" }}
          />
          <button
            onClick={onSend}
            disabled={isSending}
            className={`w-full mt-4 py-3 px-4 bg-white/10 hover:bg-white/30 border border-white/20 rounded-lg text-white font-medium transition-colors ${
              isSending ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSending ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  )

  const desktopView = (
    <div className="hidden sm:flex fixed inset-0 z-[100] items-center justify-center">
      <div className="fixed inset-0 bg-[#252525]" onClick={onClose} />
      <div className="relative w-full max-w-2xl !bg-gray-500/10 rounded-2xl animate-fadeIn backdrop-blur-md border border-white/20">
        <div className="flex items-center justify-between p-8 border-b border-white/10">
          <h2 className="text-white text-2xl font-medium">{title}</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors p-2 bg-white/10 rounded-full"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>
        <div className="p-8">
          <p className="text-white/70 text-sm mb-4">{description}</p>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="w-full min-h-[200px] bg-transparent text-white placeholder-white/50 focus:outline-none text-lg resize-none border border-white/20 rounded-lg p-4"
            autoFocus
          />
          <button
            onClick={onSend}
            disabled={isSending}
            className={`w-full mt-4 py-3 px-4 bg-white/10 hover:bg-white/30 border border-white/20 rounded-lg text-white font-medium transition-colors ${
              isSending ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSending ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {mobileView}
      {desktopView}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </>
  )
}

export const ReachOutComponent: React.FC<ReachOutComponentProps> = ({
  eventId,
  featureName,
  defaultMessage,
  isInline = false,
  buttonText = "Need Help? Reach Out",
  title = "Need Assistance?",
  description = "Our team will help you with this feature.",
  className = "",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { eventId: eventIdFromUrl } = useParams()
  const [message, setMessage] = useState(
    defaultMessage ||
      `Hi, I need assistance with ${featureName}. Could you please help me with this feature?`,
  )
  const [isSending, setIsSending] = useState(false)

  const handleSendMessage = async () => {
    if (!message?.trim()) {
      showErrorToast("Please enter a message first.")
      return
    }

    setIsSending(true)
    try {
      const sendFeedback = functionsAccessor.httpsCallable(
        "eventManagement-sendFeedbackForEvent",
      )
      const response = await sendFeedback({
        eventId: eventId || eventIdFromUrl,
        feedback: `[${featureName} Assistance Request] ${message}`,
      })

      const result = response.data as { success: boolean; message: string }
      if (result.success) {
        setMessage(
          defaultMessage ||
            `Hi, I need assistance with ${featureName}. Could you please help me with this feature?`,
        )
        setIsModalOpen(false)
        showSuccessToast("Message sent!", {
          text2: "Our team will reach out to you.",
        })
      } else {
        showErrorToast(
          result.message || "Failed to send message. Please try again.",
        )
      }
    } catch (error) {
      console.error("Error sending reach out message:", error)
      showErrorToast("Failed to send message. Please try again.")
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = () => {
    setMessage(
      defaultMessage ||
        `Hi, I need assistance with ${featureName}. Could you please help me with this feature?`,
    )
    setIsModalOpen(false)
  }

  if (isInline) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center">
          <h5 className="text-white text-lg">{title}</h5>
          <span className="text-lg ml-2">🧑‍💻</span>
        </div>
        <p className="text-white/70 text-sm">{description}</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          className="w-full min-h-[100px] bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-white/50 focus:outline-none focus:border-white/40 resize-none"
        />
        <button
          onClick={handleSendMessage}
          disabled={isSending}
          className={`w-full py-3 px-4 bg-white/20 hover:bg-white/30 border border-white/20 rounded-lg text-white font-medium transition-colors ${
            isSending ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSending ? "Sending..." : "Send Message"}
        </button>
      </div>
    )
  }

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/20 rounded-lg text-white text-sm font-medium transition-colors ${className}`}
      >
        {buttonText}
      </button>
      <ReachOutModal
        isOpen={isModalOpen}
        onClose={handleClose}
        message={message}
        setMessage={setMessage}
        isSending={isSending}
        onSend={handleSendMessage}
        title={title}
        description={description}
      />
    </>
  )
}

export default ReachOutComponent
